import type { Dayjs } from "dayjs";
import type { GoogleHealthDataType, IGoogleHealthTimeRange } from "./types";

function toRfc3339Utc(ms: number): string {
  return new Date(ms).toISOString();
}

function toCivilDayStart(date: Dayjs): string {
  return date.clone().startOf("day").format("YYYY-MM-DD");
}

function toCivilDayEnd(date: Dayjs): string {
  return date.clone().startOf("day").add(1, "day").format("YYYY-MM-DD");
}

export function toFilterDataTypeKey(dataType: GoogleHealthDataType): string {
  return dataType.replace(/-/g, "_");
}

function intervalStartFilter(
  filterKey: string,
  range: IGoogleHealthTimeRange
): string {
  const start = toRfc3339Utc(range.startMs);
  const end = toRfc3339Utc(range.endMs);
  return (
    `${filterKey}.interval.start_time >= "${start}" AND ` +
    `${filterKey}.interval.start_time < "${end}"`
  );
}

function sleepEndFilter(range: IGoogleHealthTimeRange): string {
  const start = toRfc3339Utc(range.startMs);
  const end = toRfc3339Utc(range.endMs);
  return (
    `sleep.interval.end_time >= "${start}" AND ` +
    `sleep.interval.end_time < "${end}"`
  );
}

/** ECG: só suporta `electrocardiogram.interval.start_time >=`. */
function electrocardiogramStartFilter(range: IGoogleHealthTimeRange): string {
  const start = toRfc3339Utc(range.startMs);
  return `electrocardiogram.interval.start_time >= "${start}"`;
}

function sampleCivilTimeFilter(filterKey: string, selectedDate: Dayjs): string {
  const dayStart = toCivilDayStart(selectedDate);
  const dayEnd = toCivilDayEnd(selectedDate);
  return (
    `${filterKey}.sample_time.civil_time >= "${dayStart}" AND ` +
    `${filterKey}.sample_time.civil_time < "${dayEnd}"`
  );
}

function samplePhysicalTimeFilter(
  filterKey: string,
  range: IGoogleHealthTimeRange
): string {
  const start = toRfc3339Utc(range.startMs);
  const end = toRfc3339Utc(range.endMs);
  return (
    `${filterKey}.sample_time.physical_time >= "${start}" AND ` +
    `${filterKey}.sample_time.physical_time < "${end}"`
  );
}

function heartRateSampleFilter(
  range: IGoogleHealthTimeRange,
  selectedDate?: Dayjs
): string {
  if (selectedDate) {
    return sampleCivilTimeFilter("heart_rate", selectedDate);
  }
  return samplePhysicalTimeFilter("heart_rate", range);
}

function weightSampleFilter(
  range: IGoogleHealthTimeRange,
  selectedDate?: Dayjs
): string {
  if (selectedDate) {
    return sampleCivilTimeFilter("weight", selectedDate);
  }
  return samplePhysicalTimeFilter("weight", range);
}

export function buildDataPointsFilter(
  dataType: GoogleHealthDataType,
  range: IGoogleHealthTimeRange,
  selectedDate?: Dayjs
): string {
  const filterKey = toFilterDataTypeKey(dataType);

  switch (dataType) {
    case "steps":
    case "distance":
      return intervalStartFilter(filterKey, range);
    case "sleep":
      return sleepEndFilter(range);
    case "electrocardiogram":
      return electrocardiogramStartFilter(range);
    case "heart-rate":
      return heartRateSampleFilter(range, selectedDate);
    case "weight":
      return weightSampleFilter(range, selectedDate);
    default: {
      const _exhaustive: never = dataType;
      return _exhaustive;
    }
  }
}

/** Dia civil selecionado no calendário (passos e distância). */
export function buildDayRange(selectedDate: Dayjs): IGoogleHealthTimeRange {
  const day = selectedDate.clone().startOf("day");
  return {
    startMs: day.valueOf(),
    endMs: day.add(1, "day").valueOf(),
  };
}

/** Sessões cujo despertar (`end_time`) cai no dia selecionado. */
export function buildSleepRange(selectedDate: Dayjs): IGoogleHealthTimeRange {
  const dayStart = selectedDate.clone().startOf("day");
  return {
    startMs: dayStart.valueOf(),
    endMs: dayStart.add(1, "day").valueOf(),
  };
}
